// The set-password page — where an invite or reset link lands
// (/set-password?token=…). The link is peeked first so the page can say
// whose it is, then one password sets it and signs the person in.
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Logo } from '../components/Logo';
import { redeemPasswordLink } from '../lib/auth';

type Peek = { linkType: 'invite' | 'reset'; email: string | null; displayName: string | null; expiresAt: string };

export function SetPassword() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const token = sp.get('token') ?? '';
  const [peek, setPeek] = useState<Peek | null>(null);
  const [dead, setDead] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setDead('This page needs the link you were given.');
      return;
    }
    fetch(`/api/v1/auth/password-links/${encodeURIComponent(token)}`).then(
      async (r) => {
        if (r.ok) setPeek((await r.json()) as Peek);
        else setDead(((await r.json()) as { error?: { message?: string } }).error?.message ?? 'This link is no longer valid.');
      },
      () => setDead('Could not reach the server.'),
    );
  }, [token]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== again) {
      setError('The two passwords differ.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await redeemPasswordLink(token, password);
      nav('/choose', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main style={{ maxWidth: 380, margin: '0 auto', padding: '96px 24px' }} data-section="set-password">
      <div style={{ color: 'var(--primary)', marginBottom: 28, transform: 'scale(1.6)', transformOrigin: 'left' }}>
        <Logo />
      </div>
      {dead && (
        <>
          <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 8 }}>This link has expired</h1>
          <p className="err" role="alert">{dead}</p>
          <p style={{ color: 'var(--ink-2)', fontSize: 14 }}>Ask your admin for a new one.</p>
        </>
      )}
      {peek && (
        <>
          <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.018em', marginBottom: 4 }}>{peek.linkType === 'invite' ? 'Welcome' : 'Choose a new password'}</h1>
          <p style={{ color: 'var(--ink-2)', fontSize: 14, marginBottom: 24 }} data-email={peek.email ?? ''}>
            {peek.linkType === 'invite' ? 'Set a password for ' : 'For '}<b>{peek.email}</b>
          </p>
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="field">
              <label htmlFor="password">Password</label>
              <input id="password" className="input" type="password" autoComplete="new-password" required minLength={8}
                value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="again">Password, again</label>
              <input id="again" className="input" type="password" autoComplete="new-password" required minLength={8}
                value={again} onChange={(e) => setAgain(e.target.value)} />
            </div>
            {error && <div className="err" role="alert">{error}</div>}
            <button className="btn btn-primary" disabled={busy} style={{ justifyContent: 'center' }}>
              {busy ? 'Saving…' : 'Set password and sign in'}
            </button>
          </form>
        </>
      )}
      {!dead && !peek && <div className="loading">Checking the link…</div>}
    </main>
  );
}
